import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ThemeWrapper from "./ThemeWrapper";
import Dashboard from "./components/Dashboard/Dashboard";
import AuthRoute from "./components/Auth/AuthRoute";
import MatchingOverview from "./components/Campaigns/Phases/Matching/MatchingOverview";
import { AuthStatusProvider } from "./components/Auth/AuthStatusContext";
import Layout from "./components/Common/Layout";
import AuthCallback from "./components/Auth/AuthCallback";
import Profile from "./components/Auth/Profile";
import OrganizationProfile from "./components/Organization/OrganizationProfile";
import CampaignOverview from "./components/Campaigns/CampaignOverview";
import SubscriptionSuccess from "./components/Subscription/SubscriptionSuccess";
import SubscriptionCancel from "./components/Subscription/SubscriptionCancel";
import Unauthorized from "./components/Auth/Unauthorized";
import SignOut from "./components/Auth/SignOut";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { setGlobalToast } from "./services/ApiClient";
import { setGlobalAuthTokenGetter, debugAuthTokens } from "./utils/authUtils";
import { useAuth } from "react-oidc-context";

// Component to set up global toast function
const ToastSetup: React.FC = () => {
  const { showToast } = useToast();

  useEffect(() => {
    setGlobalToast(showToast);
  }, [showToast]);

  return null;
};

// Component to set up global auth token getter
const AuthSetup: React.FC = () => {
  const auth = useAuth();

  useEffect(() => {
    const getAccessToken = () => {
      // Debug auth tokens on first load
      if (process.env.NODE_ENV === 'development') {
        debugAuthTokens();
      }

      // Try to get access token from auth.user first
      if (auth.user?.access_token) {
        return auth.user.access_token;
      }


      return null;
    };

    setGlobalAuthTokenGetter(getAccessToken);
  }, [auth]);

  return null;
};

const App: React.FC = () => {
  useEffect(() => {
    // Log application startup
    console.info('[App] MentoringFusion application started', {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      version: process.env.REACT_APP_VERSION || '1.0.0'
    });
  }, []);

  return (
    <ErrorBoundary>
      <ThemeWrapper>
        <ToastProvider>
          <ToastSetup />
          <AuthSetup />
          <Router basename={process.env.PUBLIC_URL || '/'} future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <AuthStatusProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/oauth2/idpresponse" element={<AuthCallback />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/signout" element={<SignOut />} />

                {/* All protected routes wrapped in a single AuthRoute */}
                <Route element={<AuthRoute />}>
                  {/* Dashboard routes */}
                  <Route path="/" element={<Layout><Dashboard /></Layout>} />
                  <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
                  <Route path="/profile" element={<Layout><Profile /></Layout>} />

                  {/* Organization routes */}
                  <Route path="/organizations/new" element={<Layout><OrganizationProfile /></Layout>} />
                  {/* Specific organization profile must come before generic list route */}
                  <Route path="/organizations/:id" element={<Layout><OrganizationProfile /></Layout>} />
                  {/* List all organizations (dashboard) */}
                  <Route path="/organizations" element={<Layout><Dashboard /></Layout>} />

                  {/* Subscription routes */}
                  <Route path="/subscriptions/success" element={<Layout><SubscriptionSuccess /></Layout>} />
                  <Route path="/subscriptions/cancel" element={<Layout><SubscriptionCancel /></Layout>} />

                  {/* Campaign routes */}
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id"
                    element={<Layout fullScreen><CampaignOverview /></Layout>} />

                  {/* Redirect from campaign base path to details */}
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep"
                    element={<Layout fullScreen><CampaignOverview /></Layout>} />

                  {/* Prep phase routes */}
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep/details"
                    element={<Layout fullScreen><CampaignOverview /></Layout>} />
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep/dates"
                    element={<Layout fullScreen><CampaignOverview /></Layout>} />
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep/applications"
                    element={<Layout fullScreen><CampaignOverview /></Layout>} />
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep/communication"
                    element={<Layout fullScreen><CampaignOverview /></Layout>} />
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/prep/launch"
                    element={<Layout fullScreen><CampaignOverview /></Layout>} />

                  {/* Matching phase routes */}
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/matching/application-dashboard"
                    element={<Layout fullScreen><MatchingOverview /></Layout>} />
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/matching/matching"
                    element={<Layout fullScreen><MatchingOverview /></Layout>} />
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/matching/dates"
                    element={<Layout fullScreen><MatchingOverview /></Layout>} />
                  <Route path="/organizations/:organization_id/campaigns/:campaign_id/matching/kickoff"
                    element={<Layout fullScreen><MatchingOverview /></Layout>} />

                  {/* Catch-all route to redirect to dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>

              </Routes>
            </AuthStatusProvider>
          </Router>
        </ToastProvider>
      </ThemeWrapper>
    </ErrorBoundary>
  );
};

export default App;
